import { useMemo } from 'react';
import { PROVIDER_TYPE, PAYCASH_MAX_AMOUNT } from 'constants/global';
import { useMercadoPagoVisible } from 'modules/mercadopago/useMercadoPagoVisible';

/**
 * Resolves which payment methods can be offered and which can be selected.
 * Same rules that used to live inline in ContractPayment and MultiplePayment:
 * - Card methods (BBVA, Mercado Pago) are hidden when the contract has a card block.
 * - Mercado Pago also depends on the backoffice visibility keys.
 * - PayCash is shown but disabled above PAYCASH_MAX_AMOUNT.
 * @param {{ cardBlocked?: boolean, amount?: number }} [options]
 * @returns {Object<string, { visible: boolean, disabled: boolean }>} keyed by PROVIDER_TYPE
 */
export default function usePaymentMethods({ cardBlocked = false, amount = 0 } = {}) {
  const { visible: mercadoPagoVisible } = useMercadoPagoVisible();

  return useMemo(() => ({
    [PROVIDER_TYPE.BANCOMER]: { visible: !cardBlocked, disabled: false },
    [PROVIDER_TYPE.MERCADO_PAGO]: { visible: mercadoPagoVisible && !cardBlocked, disabled: false },
    [PROVIDER_TYPE.PAYCASH]: { visible: true, disabled: (amount || 0) > PAYCASH_MAX_AMOUNT },
    [PROVIDER_TYPE.PUNTORED]: { visible: true, disabled: false },
  }), [cardBlocked, amount, mercadoPagoVisible]);
}

/**
 * True when the provider is visible and enabled in the resolved methods map.
 * @param {Object<string, { visible: boolean, disabled: boolean }>} methods
 * @param {string|null} provider
 * @returns {boolean}
 */
export const isMethodSelectable = (methods, provider) => Boolean(
  provider && methods?.[provider]?.visible && !methods[provider].disabled,
);
